import {
  CheckOperator,
  MemoryValue,
  StepCheckOperatorType,
} from '../../components/pages/Book/MemoryBank/memoryBankTypes';
import { EpisodeTextImportCommandKey } from '../../components/pages/ImportTextModal/types/episodeTextImportCommandTypes';
import { BranchStepMemoryActionType, StepTypes } from '../../dorian-shared/types/branch/BranchStep';
import {
  ExportBranch, ExportBranchStep, ExportEpisodeJson, ExportMemoryExpression, ExportStepGoto,
} from './types/ExportEpisodeTextTypes';

export class ExportEpisodeTextParser {
  private episode: ExportEpisodeJson;

  constructor(episode: ExportEpisodeJson) {
    this.episode = episode;
  }

  public toText(): string {
    const lines: string[] = [];
    const branchKeys = Object.keys(this.episode.branches);

    branchKeys.forEach((branchKey) => {
      const branch = this.episode.branches[branchKey];
      lines.push(...this.parseBranch(branchKey, branch));
    });

    return lines.join('\n');
  }

  private parseBranch(branchKey: string, branch: ExportBranch): string[] {
    const lines: string[] = [];

    if (branch.scene) {
      const sceneKey = branch.scene;
      lines.push(`${branchKey}\t\t#${EpisodeTextImportCommandKey.Location} ${sceneKey}`);
    } else {
      lines.push(`${branchKey}`);
    }

    // Parse all steps in the branch - maintain original step sequence
    if (branch.steps && branch.steps.length > 0) {
      branch.steps.forEach((step) => {
        lines.push(...this.parseStep(step));
      });
    }

    return lines;
  }

  private parseStep(step: ExportBranchStep): string[] {
    const stepType = this.getStepTypeFromString(step.type);

    switch (stepType) {
      case StepTypes.Dialogue:
        return this.parseDialogueStep(step);
      case StepTypes.Thinking:
        return this.parseThinkingStep(step);
      case StepTypes.Reaction:
        return this.parseReactionStep(step);
      case StepTypes.Narrator:
        return this.parseNarratorStep(step);
      case StepTypes.Choice:
        return this.parseChoiceStep(step);
      case StepTypes.Check:
        return this.parseCheckStep(step);
      case StepTypes.Remember:
        return this.parseRememberStep(step);
      case StepTypes.Ending:
        return this.parseEndingStep(step);
      case StepTypes.Achieve:
        return this.parseAchieveStep(step);
      case StepTypes.Texting:
        return this.parseTextingStep(step);
      default:
        return [`\t# Unsupported step type: ${step.type || stepType}`];
    }
  }

  private getStepTypeFromString(type: string): StepTypes | undefined {
    switch (type.toLowerCase()) {
      case 'dialogue':
        return StepTypes.Dialogue;
      case 'narrator':
        return StepTypes.Narrator;
      case 'choice':
        return StepTypes.Choice;
      case 'check':
        return StepTypes.Check;
      case 'remember':
        return StepTypes.Remember;
      case 'ending':
        return StepTypes.Ending;
      case 'achieve':
        return StepTypes.Achieve;
      case 'thinking':
        return StepTypes.Thinking;
      case 'reaction':
        return StepTypes.Reaction;
      case 'texting':
        return StepTypes.Texting;
      default:
        return undefined;
    }
  }

  private parseDialogueStep(step: ExportBranchStep): string[] {
    const lines: string[] = [];
    const characterName = step.character ? step.character.alias : 'Character_unknown';

    // Do not include dialogue command for now
    const command = this.parseExpression(undefined, step.character?.expression ?? '');

    lines.push(`${characterName}\t${step.text}\t${command}`);

    if (step.goto?.branch) {
      lines.push(this.parseGoto(step.goto));
    }

    return lines;
  }

  private parseThinkingStep(step: ExportBranchStep): string[] {
    const lines: string[] = [];
    const characterName = step.character ? step.character.alias : 'Character_unknown';

    const command = this.parseExpression(EpisodeTextImportCommandKey.StepThink, step.character?.expression ?? '');

    lines.push(`${characterName}\t${step.text}\t${command}`);

    if (step.goto?.branch) {
      lines.push(this.parseGoto(step.goto));
    }

    return lines;
  }

  private parseReactionStep(step: ExportBranchStep): string[] {
    const lines: string[] = [];
    const characterName = step.character ? step.character.alias : 'Character_unknown';

    const command = this.parseExpression(EpisodeTextImportCommandKey.StepReact, step.character?.expression ?? '');

    lines.push(`${characterName}\t${step.text}\t${command}`);

    if (step.goto?.branch) {
      lines.push(this.parseGoto(step.goto));
    }

    return lines;
  }

  private parseNarratorStep(step: ExportBranchStep): string[] {
    const lines: string[] = [];

    lines.push(`\t${step.text}`);

    if (step.goto?.branch) {
      lines.push(this.parseGoto(step.goto));
    }

    return lines;
  }

  private parseChoiceStep(step: ExportBranchStep): string[] {
    const lines: string[] = [];

    const characterName = step.character ? step.character.alias : 'Character_unknown';

    // Do not include choice command for now
    const command = this.parseExpression(undefined, step.character?.expression ?? '');

    lines.push(`${characterName}\t#${EpisodeTextImportCommandKey.StepChoice} ${step.text}\t${command}`);

    if (step.answers && step.answers.length > 0) {
      step.answers.forEach((answer) => {
        const answerText = `\t${answer.text}`;
        const answerGoto = answer.goto?.branch ? `\t#${EpisodeTextImportCommandKey.Goto} ${answer.goto.branch}` : '';
        lines.push(`${answerText} ${answerGoto}`);
      });
    }

    return lines;
  }

  private parseCheckStep(step: ExportBranchStep): string[] {
    const lines: string[] = [];

    if (!step.check) {
      return lines;
    }

    const stepCheck = step.check;

    let checkLine = `\t#${EpisodeTextImportCommandKey.StepCheck}: `;

    if (stepCheck.operator === StepCheckOperatorType.Min || step.check.operator === StepCheckOperatorType.Max) {
      if (typeof stepCheck.value === 'object' && Array.isArray(stepCheck.value.variable)) {
        checkLine += `${stepCheck.operator.toUpperCase()} (${stepCheck.value.variable.join(', ')})`;
        if (step.switch && step.switch.length > 0) {
          step.switch.forEach((s) => {
            const switchValue = this.resolveExpression(s.value);
            checkLine += `, if ${switchValue} ${EpisodeTextImportCommandKey.Goto} ${s.goto?.branch ?? ''}`;
          });
        }
      }
    } else {
      const operator = CheckOperator[stepCheck.operator];
      const value = typeof stepCheck.value === 'object' ? stepCheck.value.variable : this.resolveExpression(stepCheck.value);
      checkLine += `if ${stepCheck.variable} ${operator.short} ${value}`;
      if (step.switch && step.switch.length > 0) {
        const trueCase = step.switch.find((s) => s.value === true);
        const falseCase = step.switch.find((s) => s.value === false);
        checkLine += `, if Yes goto ${trueCase?.goto?.branch ?? ''}, if No goto ${falseCase?.goto?.branch ?? ''}`;
      }
    }
    lines.push(checkLine);
    return lines;
  }

  private parseRememberStep(step: ExportBranchStep): string[] {
    const lines: string[] = [];

    if (step.action) {
      const variableName = this.resolveVariable(step.action.variable);

      switch (step.action.type) {
        case BranchStepMemoryActionType.Set: {
          const actionPrefix = '=';
          const value = this.resolveExpression(step.action.value);
          lines.push(`\t#${EpisodeTextImportCommandKey.StepRemember} ${variableName} ${actionPrefix} ${value}`);
          break;
        }
        case BranchStepMemoryActionType.Increase: {
          const actionPrefix = '+';
          const increaseValue = this.resolveExpression(step.action.value);
          lines.push(`\t#${EpisodeTextImportCommandKey.StepRemember} ${variableName} ${actionPrefix} ${increaseValue}`);
          break;
        }
        case BranchStepMemoryActionType.Decrease: {
          const actionPrefix = '-';
          const decreaseValue = this.resolveExpression(step.action.value);
          lines.push(`\t#${EpisodeTextImportCommandKey.StepRemember} ${variableName} ${actionPrefix} ${decreaseValue}`);
          break;
        }
        case BranchStepMemoryActionType.View: {
          const viewValues = Array.isArray(step.action.value)
            ? step.action.value.join(', ')
            : String(step.action.value);
          lines.push(`\t# Unsupported: remember ${variableName} view [${viewValues}]`);
          break;
        }
        default:
          lines.push(`\t# Unsupported action type: ${step.action.type}`);
          break;
      }
    }

    if (step.goto?.branch) {
      lines.push(this.parseGoto(step.goto));
    }

    return lines;
  }

  private parseEndingStep(step: ExportBranchStep): string[] {
    const lines: string[] = [];

    lines.push(`#${EpisodeTextImportCommandKey.StepEnding}\t${step.text ? ` ${step.text}` : ''}`);

    return lines;
  }

  private parseAchieveStep(step: ExportBranchStep): string[] {
    const lines: string[] = [];

    if (step.achievementId) {
      lines.push(`\t# Unsupported: achieve ${step.achievementId}`);
    }

    if (step.goto?.branch) {
      lines.push(this.parseGoto(step.goto));
    }

    return lines;
  }

  private parseTextingStep(step: ExportBranchStep): string[] {
    const lines: string[] = [];
    const characterName = step.character ? step.character.alias : 'Character_unknown';

    lines.push(`${characterName}\t${step.text}\t#${EpisodeTextImportCommandKey.StepChat}`);

    if (step.goto?.branch) {
      lines.push(this.parseGoto(step.goto));
    }

    return lines;
  }

  private parseGoto(goto: ExportStepGoto): string {
    return `\t#${EpisodeTextImportCommandKey.Goto} ${goto.branch}`;
  }

  private resolveVariable(variableId: number): string {
    // In a real implementation, this would look up the variable name from the episode data
    // For now, we'll return a placeholder
    return `${variableId}`;
  }

  private resolveExpression(value: MemoryValue | ExportMemoryExpression | any): string {
    if (typeof value === 'boolean') {
      return value ? 'yes' : 'no';
    }

    if (typeof value === 'string' || typeof value === 'number') {
      return String(value);
    }

    if (typeof value === 'object' && value !== null) {
      if (value.type === 'variable') {
        return `${value.variable}`;
      } if (value.type === 'variable[]') {
        return `[${(value.variable as string[]).join(', ')}]`;
      }
    }

    return String(value);
  }

  private parseExpression(commandKey: EpisodeTextImportCommandKey | undefined, expression: string): string {
    let command = commandKey ? `#${commandKey}` : '';

    const isExpressionValid = Object.entries(EpisodeTextImportCommandKey)
      .some(([key, value]) => key.startsWith('Expression') && value === expression);

    if (isExpressionValid) {
      command += ` #${expression}`;
    }

    return command.trim();
  }
}
